import { Search, X } from "lucide-react";
import { useMemo, useRef, useState, type KeyboardEvent } from "react";

export interface PickableCandidate {
  id: string;
  username: string;
  full_name: string | null;
}

/**
 * Type a name, pick a candidate.
 *
 * Filters LOCALLY rather than over the wire, unlike
 * `MergeWorksheetAutocomplete`: the candidate list is already loaded for
 * the assign dropdown, and a clinic hires in tens, not thousands. A
 * debounced round-trip per keystroke would be machinery for a list that
 * fits in memory twice over.
 *
 * Once chosen it collapses to a clearable chip, so the filter that is in
 * force is always visible — a filtered list that looks unfiltered is how
 * somebody concludes an exam has gone missing.
 */
export function CandidatePicker({
  candidates,
  selected,
  onSelect,
  onClear,
  placeholder = "Filter by candidate…",
}: {
  candidates: PickableCandidate[];
  selected: PickableCandidate | null;
  onSelect: (c: PickableCandidate) => void;
  onClear: () => void;
  placeholder?: string;
}) {
  const [query, setQuery] = useState("");
  const [openList, setOpenList] = useState(false);
  const [active, setActive] = useState(0);
  const blurTimer = useRef<number | null>(null);

  const nameOf = (c: PickableCandidate) => c.full_name || c.username;

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const pool = needle
      ? candidates.filter(
          (c) =>
            nameOf(c).toLowerCase().includes(needle) ||
            c.username.toLowerCase().includes(needle),
        )
      : candidates;
    return pool.slice(0, 8);
  }, [candidates, query]);

  if (selected) {
    return (
      <span
        data-cy="candidate-filter-chip"
        className="flex items-center gap-2 rounded-md border border-accent bg-accent-light px-2 py-1 text-xs text-accent"
      >
        {nameOf(selected)}
        <button
          type="button"
          data-cy="btn-clear-candidate-filter"
          aria-label="Clear candidate filter"
          onClick={() => {
            setQuery("");
            onClear();
          }}
          className="rounded hover:opacity-70"
        >
          <X aria-hidden className="h-3.5 w-3.5" />
        </button>
      </span>
    );
  }

  const choose = (c: PickableCandidate) => {
    setOpenList(false);
    setQuery("");
    onSelect(c);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpenList(true);
      setActive((i) => Math.min(i + 1, matches.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const pick = matches[active];
      if (pick) choose(pick);
    } else if (e.key === "Escape") {
      setOpenList(false);
    }
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-1.5 rounded-md border border-input-border bg-input-bg px-2 py-1">
        <Search aria-hidden className="h-3.5 w-3.5 text-foreground-muted" />
        <input
          type="search"
          data-cy="candidate-filter"
          value={query}
          placeholder={placeholder}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
            setOpenList(true);
          }}
          onFocus={() => setOpenList(true)}
          onKeyDown={onKeyDown}
          // A click on a suggestion blurs the input first, so closing has
          // to wait long enough for the click to land.
          onBlur={() => {
            blurTimer.current = window.setTimeout(
              () => setOpenList(false),
              150,
            );
          }}
          className="w-48 bg-transparent text-xs text-foreground outline-none"
        />
      </div>

      {openList && matches.length > 0 && (
        <ul
          data-cy="candidate-filter-options"
          className="absolute right-0 z-20 mt-1 max-h-60 w-56 overflow-auto rounded-md border border-border bg-background-card py-1 shadow-theme-lg"
        >
          {matches.map((c, i) => (
            <li key={c.id}>
              <button
                type="button"
                data-cy="candidate-filter-option"
                onMouseDown={() => {
                  // mousedown, not click: blur fires first otherwise and
                  // the list is gone before the click arrives.
                  if (blurTimer.current) window.clearTimeout(blurTimer.current);
                  choose(c);
                }}
                onMouseEnter={() => setActive(i)}
                className={[
                  "flex w-full flex-col items-start px-3 py-1.5 text-left text-xs",
                  i === active
                    ? "bg-table-row-hover text-foreground"
                    : "text-foreground-muted hover:text-foreground",
                ].join(" ")}
              >
                <span className="text-foreground">{nameOf(c)}</span>
                <span className="text-[11px] text-foreground-muted">
                  {c.username}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {openList && query.trim() && matches.length === 0 && (
        <p
          data-cy="candidate-filter-empty"
          className="absolute right-0 z-20 mt-1 w-56 rounded-md border border-border bg-background-card px-3 py-2 text-xs text-foreground-muted shadow-theme-lg"
        >
          No candidate matches “{query.trim()}”.
        </p>
      )}
    </div>
  );
}
